import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Megaphone, Plus, Pencil, Trash2, ToggleLeft, ToggleRight,
  Search, Filter, Globe, Target
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";

const PLATFORMS = [
  { value: "meta", label: "Meta / Facebook" },
  { value: "google", label: "Google Ads" },
  { value: "website", label: "Website" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "referral", label: "Referral" },
  { value: "channel_partner", label: "Channel Partner" },
  { value: "walk_in", label: "Walk-in" },
  { value: "property_portal", label: "Property Portal" },
];

const PRIORITIES = [
  { value: "high", label: "High", color: "bg-red-100 text-red-700" },
  { value: "medium", label: "Medium", color: "bg-amber-100 text-amber-700" },
  { value: "low", label: "Low", color: "bg-slate-100 text-slate-600" },
];

const emptyForm = {
  name: "",
  platform: "website",
  project: "",
  source: "",
  sub_source: "",
  default_routing_rule: "",
  priority: "medium",
  active: true,
};

const CampaignsPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [routingRules, setRoutingRules] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("all");

  const fetchData = async () => {
    try {
      const [campRes, rulesRes, projRes] = await Promise.all([
        axios.get(`${API}/campaigns`),
        axios.get(`${API}/routing/rules`).catch(() => ({ data: [] })),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
      ]);
      setCampaigns(campRes.data);
      setRoutingRules(rulesRes.data);
      setProjects(Array.isArray(projRes.data) ? projRes.data : []);
    } catch (err) {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditingCampaign(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (camp) => {
    setEditingCampaign(camp);
    setForm({
      name: camp.name || "",
      platform: camp.platform || "website",
      project: camp.project || "",
      source: camp.source || "",
      sub_source: camp.sub_source || "",
      default_routing_rule: camp.default_routing_rule || "",
      priority: camp.priority || "medium",
      active: camp.active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Campaign name is required"); return; }
    try {
      if (editingCampaign) {
        await axios.put(`${API}/campaigns/${editingCampaign.campaign_id}`, form);
        toast.success("Campaign updated");
      } else {
        await axios.post(`${API}/campaigns`, form);
        toast.success("Campaign created");
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save campaign");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`);
      toast.success("Campaign deleted");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleActive = async (camp) => {
    try {
      await axios.put(`${API}/campaigns/${camp.campaign_id}`, { active: !camp.active });
      fetchData();
    } catch (err) {
      toast.error("Failed to toggle status");
    }
  };

  const filtered = campaigns.filter((c) => {
    const matchSearch = c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.source?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchPlatform = filterPlatform === "all" || c.platform === filterPlatform;
    return matchSearch && matchPlatform;
  });

  const getPriorityBadge = (p) => {
    const prio = PRIORITIES.find((x) => x.value === p) || PRIORITIES[1];
    return <Badge variant="outline" className={prio.color}>{prio.label}</Badge>;
  };

  const getPlatformLabel = (v) => PLATFORMS.find((p) => p.value === v)?.label || v;

  const getRuleName = (ruleId) => {
    const rule = routingRules.find((r) => r.rule_id === ruleId);
    return rule ? rule.name : "—";
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="campaigns-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Campaigns</h1>
            <p className="text-sm text-slate-500 mt-1">Manage campaign-to-routing rule mappings</p>
          </div>
          <Button onClick={openCreate} data-testid="create-campaign-btn">
            <Plus className="w-4 h-4 mr-2" /> New Campaign
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="campaigns-total">{campaigns.length}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Active</p>
            <p className="text-2xl font-bold text-emerald-600">{campaigns.filter(c => c.active).length}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Platforms</p>
            <p className="text-2xl font-bold text-blue-600">{new Set(campaigns.map(c => c.platform)).size}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">With Rules</p>
            <p className="text-2xl font-bold text-amber-600">{campaigns.filter(c => c.default_routing_rule).length}</p>
          </CardContent></Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search campaigns..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10" data-testid="campaigns-search" />
          </div>
          <Select value={filterPlatform} onValueChange={setFilterPlatform}>
            <SelectTrigger className="w-48" data-testid="campaigns-platform-filter">
              <Filter className="w-4 h-4 mr-2 text-slate-400" /><SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              {PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-slate-400">Loading campaigns...</div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No campaigns found. Create your first campaign.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Routing Rule</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((camp) => (
                    <TableRow key={camp.campaign_id} data-testid={`campaign-row-${camp.campaign_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{camp.name}</p>
                          {camp.source && <p className="text-xs text-slate-500">Source: {camp.source}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          <Globe className="w-3 h-3 mr-1" />{getPlatformLabel(camp.platform)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">{camp.project || "—"}</TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{getRuleName(camp.default_routing_rule)}</span>
                      </TableCell>
                      <TableCell>{getPriorityBadge(camp.priority)}</TableCell>
                      <TableCell>
                        <button onClick={() => toggleActive(camp)} className="flex items-center gap-1.5" data-testid={`campaign-toggle-${camp.campaign_id}`}>
                          {camp.active
                            ? <><ToggleRight className="w-5 h-5 text-emerald-500" /><span className="text-xs text-emerald-600">Active</span></>
                            : <><ToggleLeft className="w-5 h-5 text-slate-400" /><span className="text-xs text-slate-400">Inactive</span></>}
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(camp)} data-testid={`campaign-edit-${camp.campaign_id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(camp.campaign_id)}
                            className="text-red-500 hover:text-red-700" data-testid={`campaign-delete-${camp.campaign_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create / Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="campaign-dialog">
            <DialogHeader>
              <DialogTitle>{editingCampaign ? "Edit Campaign" : "New Campaign"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Campaign Name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Meta Leads - Skyline Project" data-testid="campaign-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Platform</label>
                  <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                    <SelectTrigger data-testid="campaign-platform-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Priority</label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="campaign-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Project</label>
                <Input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}
                  placeholder="e.g. Skyline Heights" data-testid="campaign-project-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Source</label>
                  <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                    placeholder="e.g. facebook" data-testid="campaign-source-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Sub-source</label>
                  <Input value={form.sub_source} onChange={(e) => setForm({ ...form, sub_source: e.target.value })}
                    placeholder="e.g. lead_ad_v2" data-testid="campaign-subsource-input" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Default Routing Rule</label>
                <Select value={form.default_routing_rule || "_none"} onValueChange={(v) => setForm({ ...form, default_routing_rule: v === "_none" ? "" : v })}>
                  <SelectTrigger data-testid="campaign-rule-select"><SelectValue placeholder="Select a rule" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No default rule</SelectItem>
                    {routingRules.map((r) => <SelectItem key={r.rule_id} value={r.rule_id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="campaign-save-btn">
                {editingCampaign ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CampaignsPage;
